/**
 * Social Media Manager - Real BGV Integration
 * 
 * This component replaces the mock social media management with real BGV API integration.
 * Features real-time post management, profile management, and Instagram publishing.
 */

import React, { useState, useCallback } from 'react'
import { Plus, Filter, Search, RefreshCw, Settings, AlertCircle, CheckCircle2, TrendingUp } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'

// Import BGV-integrated components
import PostCreateDialog from '@/features/social/components/PostCreateDialog'
import PostList from '@/features/social/components/PostList'
import ProfileSelector from '@/features/social/components/ProfileSelector'
import PlatformStats from '@/features/social/components/PlatformStats'

// Import BGV-integrated hooks
import {
  usePosts,
  useProfiles,
  useDeletePost,
  usePublishPost,
  useProcessScheduledPosts,
  useBGVConnectionHealth,
  useScheduledPosts
} from '@/features/social/hooks/useSocialMedia'

import { PostFilters, SocialPost, Platform } from '@/types/social'
// import { toast } from '@/hooks/use-toast'"

export default function SocialMediaManager() {
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [selectedProfile, setSelectedProfile] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null)

  // BGV Integration hooks
  const { data: connectionHealth, refetch: refetchHealth } = useBGVConnectionHealth()
  const { data: profiles = [], isLoading: profilesLoading, error: profilesError } = useProfiles()
  const { data: scheduledPosts = [] } = useScheduledPosts()

  // Build filters for posts query
  const postFilters: PostFilters = {
    ...(statusFilter !== 'all' && { status: [statusFilter as any] }),
    ...(selectedProfile !== 'all' && { profiles: [selectedProfile] })
  }

  const { 
    data: posts = [], 
    isLoading: postsLoading, 
    error: postsError,
    refetch: refetchPosts 
  } = usePosts(postFilters)

  // Mutations
  const deletePostMutation = useDeletePost()
  const publishPostMutation = usePublishPost()
  const processScheduledMutation = useProcessScheduledPosts()

  // Filter posts by search query
  const filteredPosts = posts.filter(post => 
    searchQuery === '' || 
    post.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.caption?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.hashtags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  // Event handlers
  const handleCreatePost = useCallback(() => {
    setShowCreateDialog(true)
  }, [])

  const handleEditPost = useCallback((post: SocialPost) => {
    setSelectedPost(post)
    setShowCreateDialog(true)
  }, [])

  const handleDeletePost = useCallback(async (postId: string) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deletePostMutation.mutateAsync(postId)
      } catch (error) {
        console.error('Failed to delete post:', error)
      }
    }
  }, [deletePostMutation])

  const handleDuplicatePost = useCallback((post: SocialPost) => {
    setSelectedPost({
      ...post,
      id: '', // Clear ID to create new post
      status: 'DRAFT',
      scheduled_time: null,
      published_at: null
    })
    setShowCreateDialog(true)
  }, [])

  const handlePublishPost = useCallback(async (post: SocialPost, testMode: boolean = true) => {
    try {
      await publishPostMutation.mutateAsync({ 
        postId: post.id, 
        testMode 
      })
    } catch (error) {
      console.error('Failed to publish post:', error)
    }
  }, [publishPostMutation])

  const handleProcessScheduled = useCallback(async () => {
    try {
      await processScheduledMutation.mutateAsync()
    } catch (error) {
      console.error('Failed to process scheduled posts:', error)
    }
  }, [processScheduledMutation])

  const handleRefresh = useCallback(() => {
    refetchPosts()
    refetchHealth()
  }, [refetchPosts, refetchHealth])

  // Get connection status color
  const getConnectionStatusColor = () => {
    if (!connectionHealth) return 'bg-gray-500'
    switch (connectionHealth.status) {
      case 'healthy': return 'bg-green-500'
      case 'degraded': return 'bg-yellow-500'
      case 'unhealthy': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Social Media Manager</h1>
          <p className="text-muted-foreground">
            Create, schedule, and manage social media posts across all platforms
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* BGV Connection Status */}
          <div className="flex items-center gap-2 px-3 py-1 bg-muted/30 rounded-lg">
            <div className={`w-2 h-2 rounded-full ${getConnectionStatusColor()}`} />
            <span className="text-xs font-medium">
              BGV: {connectionHealth?.status || 'Unknown'}
            </span>
          </div>
          
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          
          <Button onClick={handleCreatePost}>
            <Plus className="h-4 w-4 mr-2" />
            Create Post
          </Button>
        </div>
      </div>

      {/* Connection Health Alert */}
      {connectionHealth && connectionHealth.status !== 'healthy' && (
        <Alert variant={connectionHealth.status === 'unhealthy' ? 'destructive' : 'default'}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {connectionHealth.message}
            {connectionHealth.status === 'unhealthy' && (
              <Button 
                variant="link" 
                className="p-0 h-auto ml-2"
                onClick={refetchHealth}
              >
                Retry connection
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Error Alerts */}
      {profilesError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load profiles: {profilesError.message}
          </AlertDescription>
        </Alert>
      )}

      {postsError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load posts: {postsError.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Platform Stats */}
      <PlatformStats 
        posts={posts}
        profiles={profiles}
      />

      {/* Quick Actions */}
      {scheduledPosts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Scheduled Posts</CardTitle>
                <CardDescription>
                  {scheduledPosts.length} posts ready to be published
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                onClick={handleProcessScheduled}
                disabled={processScheduledMutation.isPending}
              >
                {processScheduledMutation.isPending ? 'Processing...' : 'Process All'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4" />
              Ready to publish {scheduledPosts.length} scheduled posts
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and Search */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                  <SelectItem value="PUBLISHED">Published</SelectItem>
                  <SelectItem value="ERROR">Failed</SelectItem>
                </SelectContent>
              </Select>

              <ProfileSelector
                profiles={profiles}
                selectedProfile={selectedProfile}
                onProfileChange={setSelectedProfile}
                placeholder="All Profiles"
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Main Content */}
      <Tabs defaultValue="posts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="posts">
            Posts ({filteredPosts.length})
          </TabsTrigger>
          <TabsTrigger value="analytics" disabled>
            Analytics
          </TabsTrigger>
          <TabsTrigger value="settings" disabled>
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="posts">
          {postsLoading || profilesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Loading posts...</p>
              </div>
            </div>
          ) : (
            <PostList
              posts={filteredPosts}
              profiles={profiles}
              onEdit={handleEditPost}
              onDelete={handleDeletePost}
              onDuplicate={handleDuplicatePost}
              onViewAnalytics={(post) => console.log('Analytics not available for post:', post.id)}
            />
          )}
        </TabsContent>

        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <CardTitle>Analytics</CardTitle>
              <CardDescription>
                Social media analytics and insights
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Analytics Coming Soon</h3>
                <p className="text-muted-foreground">
                  Social media analytics will be available in a future update.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Settings</CardTitle>
              <CardDescription>
                Configure social media automation and preferences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <Settings className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Settings Coming Soon</h3>
                <p className="text-muted-foreground">
                  Social media settings will be available in a future update.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Post Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedPost?.id ? 'Edit Post' : 'Create New Post'}
            </DialogTitle>
          </DialogHeader>
          <PostCreateDialog
            onClose={() => {
              setShowCreateDialog(false)
              setSelectedPost(null)
            }}
            initialData={selectedPost ? {
              content: selectedPost.content,
              caption: selectedPost.caption || '',
              media: selectedPost.media || [],
              platforms: [profiles.find(p => p.id === selectedPost.profile_id)?.platform || 'INSTAGRAM'],
              scheduled_time: selectedPost.scheduled_time || '',
              hashtags: selectedPost.hashtags || [],
              mentions: selectedPost.mentions || [],
              tags: selectedPost.tags || []
            } : undefined}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}